import React, { Component, lazy, memo, Suspense } from 'react';
import { Button, Loader } from '@Sage/hoi-poi-ui';
import {
  clearChunkReloadFlag,
  getChunkLoadCause,
  isChunkLoadError,
  tryReloadForStaleChunk,
} from '@Sage/ssm-utils';

import Web5Context, { useWeb5Context } from 'lib/Web5Context';

import { RICH_TEXT_OVERRIDES, richTextPropTypes } from './contract';
import { loadRichTextEditor } from './loadRichTextEditor';

const RichTextEditor = lazy(loadRichTextEditor);

const reloadPage = () => window.location.reload();

const RichTextReloadAction = memo(({ onReload }) => {
  const { t } = useWeb5Context();
  return (
    <Button onClick={onReload} data-testid="rich-text-chunk-reload">
      {t('action_refresh')}
    </Button>
  );
});

RichTextReloadAction.displayName = 'RichTextReloadAction';

class RichTextChunkErrorBoundary extends Component {
  state = { error: null, selfHealing: false };

  static contextType = Web5Context;

  static getDerivedStateFromError(error) {
    return { error, selfHealing: false };
  }

  componentDidCatch(error) {
    if (!isChunkLoadError(error)) return;

    if (tryReloadForStaleChunk(error)) {
      this.setState({ selfHealing: true });
      return;
    }

    clearChunkReloadFlag();
    this.context?.reportError?.(error, {
      chunkLoad: true,
      cause: getChunkLoadCause(error) || 'unknown',
      selfHealExhausted: true,
    });
  }

  render() {
    const { children, onReload } = this.props;
    const { error, selfHealing } = this.state;

    if (!error) return children;
    if (!isChunkLoadError(error)) throw error;
    if (selfHealing) return <Loader size="big" />;

    return (
      <div
        data-testid="rich-text-chunk-error"
        style={{ display: 'flex', justifyContent: 'center', padding: 16 }}
      >
        <RichTextReloadAction onReload={onReload} />
      </div>
    );
  }
}

const RichText = memo((props) => {
  return (
    <RichTextChunkErrorBoundary onReload={reloadPage}>
      <Suspense fallback={<Loader size="big" />}>
        <RichTextEditor {...props} />
      </Suspense>
    </RichTextChunkErrorBoundary>
  );
});

RichText.displayName = 'RichText';
RichText.overrides = RICH_TEXT_OVERRIDES;
RichText.propTypes = richTextPropTypes;

export { RichTextContext } from './contract';
export default RichText;
